/**
 * ErrorFallback Component
 * Enhanced error fallback UI with retry functionality and error reporting
 * Provides better user experience when errors occur
 */
import { useState } from 'react';
import Button from './Button';
import { errorMonitoringService } from '../../services/errorMonitoringService';
import { apiService } from '../../services/apiService';
import { logger } from '../../utils/logger';

interface ErrorFallbackProps {
  error: Error;
  resetErrorBoundary: () => void;
  componentStack?: string;
}

export default function ErrorFallback({ 
  error, 
  resetErrorBoundary, 
  componentStack 
}: ErrorFallbackProps) {
  const [isReporting, setIsReporting] = useState(false);
  const [hasReported, setHasReported] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);

  const handleReportError = async () => {
    if (hasReported || isReporting) return;

    setIsReporting(true);
    setReportError(null);
    try {
      const pageState = {
        componentStack: componentStack || '',
        timestamp: new Date().toISOString(),
        path: window.location.pathname
      };

      await apiService.post('/error-reports', {
        url: window.location.href,
        error_message: error.message || 'Unknown error',
        stack_trace: error.stack || null,
        browser_info: navigator.userAgent,
        page_state: JSON.stringify(pageState)
      });

      errorMonitoringService.captureReactError(error, {
        componentStack: componentStack || ''
      });

      setHasReported(true);
    } catch (err) {
      logger.child('UI').error('Failed to submit error report:', err);
      setReportError('Failed to submit error report. Please try again.');
    } finally {
      setIsReporting(false);
    }
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
        {/* Error Icon */}
        <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-6">
          <svg 
            className="h-8 w-8 text-red-600" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" 
            />
          </svg>
        </div>

        {/* Error Message */}
        <h1 className="text-xl font-bold text-gray-900 mb-2">
          Oops! Something went wrong
        </h1>
        <p className="text-gray-600 mb-6">
          We encountered an unexpected error. Don't worry, Use below option to notify the issue and our team will fix it.
        </p>

        {/* Error Details (Development) */}
        {process.env.NODE_ENV === 'development' && (
          <div className="bg-gray-100 rounded-lg p-4 mb-6 text-left">
            <h3 className="text-sm font-medium text-gray-900 mb-2">Error Details:</h3>
            <p className="text-xs text-gray-700 font-mono break-all">
              {error.message}
            </p>
            {error.stack && (
              <details className="mt-2">
                <summary className="text-xs text-gray-600 cursor-pointer">Stack Trace</summary>
                <pre className="text-xs text-gray-600 mt-1 whitespace-pre-wrap">
                  {error.stack}
                </pre>
              </details>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="space-y-3">
          <Button 
            onClick={resetErrorBoundary}
            className="w-full"
          >
            Try Again
          </Button>

          <Button 
            variant="outline" 
            onClick={handleRefresh}
            className="w-full"
          >
            Refresh Page
          </Button>

          {!hasReported && (
            <Button 
              variant="ghost" 
              onClick={handleReportError}
              isLoading={isReporting}
              className="w-full text-sm"
            >
              {isReporting ? 'Reporting...' : 'Report This Error'}
            </Button>
          )}

          {hasReported && (
            <p className="text-sm text-green-600">
              ✓ Error reported successfully
            </p>
          )}

          {reportError && (
            <p className="text-xs text-red-600">
              {reportError}
            </p>
          )}
        </div>

        {/* Help Text */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <p className="text-xs text-gray-500">
            If this problem persists, please contact support or try refreshing the page.
          </p>
        </div>
      </div>
    </div>
  );
}